import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const BedsMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [bedsList, setBedsList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialFormState = {
    bed_id: "",
    room_no: "",
    bed_no: "",
    location: "",
    room_type: "",
    rate: "",
    status: "Available",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- 1. SEARCH LOGIC ---
  const searchBeds = async (text) => {
    setSearchText(text);
    if (text.length < 1) {
      setBedsList([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/beds/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setBedsList(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectBed = (bed) => {
    setFormData({ ...bed, _isExisting: true });
    setSearchText(`ID: ${bed.bed_id} | Room: ${bed.room_no}`);
    setShowDropdown(false);
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS (Auto-Increment Preview) ---
  const clearForm = async () => {
    try {
      // Auto-fetch the next ID for preview purposes
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/beds?employee_id=${user?.id}`);
      const allBeds = await res.json();
      const lastId = Math.max(...allBeds.map(b => parseInt(b.bed_id) || 0), 0);
      
      setFormData({
        ...initialFormState,
        bed_id: (lastId + 1).toString()
      });
    } catch (err) {
      setFormData({ ...initialFormState });
    }
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.room_no || !formData.bed_no || !formData.room_type) {
      return alert("Room Number, Bed Number, and Type are required!");
    }

    const payload = { ...formData, created_by: user?.id };
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/beds" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/beds/${formData.bed_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Bed Master Record Saved!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving bed details (Check if Room/Bed combination already exists)");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  // --- 4. PRINT FORM ---
// --- 4. PRINT FORM ---
const printForm = () => {
  const newWindow = window.open("", "", "width=900,height=700");
  const today = new Date().toLocaleDateString();

  const htmlContent = `
    <html>
      <head>
        <title>Bed Profile - ${formData.bed_id}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2980b9; padding-bottom: 10px; margin-bottom: 20px; }
          .hospital-info h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
          .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
          .report-title { text-align: right; }
          .report-title h2 { margin: 0; color: #2980b9; font-size: 18px; }
          .section-header { background: #ecf0f1; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #2980b9; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
          .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
          .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
          .label { font-weight: 600; width: 160px; color: #555; }
          .value { flex: 1; color: #000; font-weight: 500; }
          .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
        </style>
      </head>

      <body onload="window.print(); window.close();">

        <div class="header">
          <div class="hospital-info">
            <h1>EMEDHA CARE HOSPITAL</h1>
            <p>Inpatient Bed Master Record</p>
          </div>
          <div class="report-title">
            <h2>BED PROFILE</h2>
            <p>Print Date: ${today}</p>
          </div>
        </div>

        <div class="section-header">Bed Information</div>
        <div class="data-grid">
          <div class="data-item"><span class="label">Bed ID:</span> <span class="value">${formData.bed_id}</span></div>
          <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
          <div class="data-item"><span class="label">Room Number:</span> <span class="value">${formData.room_no}</span></div>
          <div class="data-item"><span class="label">Bed Number:</span> <span class="value">${formData.bed_no}</span></div>
          <div class="data-item"><span class="label">Room Type:</span> <span class="value">${formData.room_type}</span></div>
          <div class="data-item"><span class="label">Location:</span> <span class="value">${formData.location || 'N/A'}</span></div>
        </div>

        <div class="section-header">Billing Details</div>
        <div class="data-grid">
          <div class="data-item"><span class="label">Daily Rate:</span> <span class="value">₹ ${parseFloat(formData.rate || 0).toFixed(2)}</span></div>
        </div>

        <div class="footer">
          System Generated Report - Emedha HMS
        </div>

      </body>
    </html>
  `;

  newWindow.document.write(htmlContent);
  newWindow.document.close();
};

  return (
    <div className="registration-container fade-in">
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Bed or Room</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID, Room, or Type..."
            onChange={(e) => searchBeds(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && bedsList.length === 1) selectBed(bedsList[0]);
            }}
            autoFocus
          />
          {showDropdown && bedsList.length > 0 && (
            <div className="patient-dropdown">
              {bedsList.map((b) => (
                <div key={b.bed_id} className="patient-item" onClick={() => selectBed(b)}>
                  ID: {b.bed_id} | Room: {b.room_no} | {b.room_type}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        <div className="form-section-header">
          <h3>Bed Identification</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Bed ID</label>
            <input type="text" value={formData.bed_id} readOnly placeholder="Auto Generated" />
          </div>
          <div className="input-box">
            <label>Room Number <span style={{color:'red'}}>*</span></label>
            <input type="text" name="room_no" value={formData.room_no} onChange={handleChange} disabled={isDisabled} placeholder="e.g., 201" />
          </div>
          <div className="input-box">
            <label>Bed Number <span style={{color:'red'}}>*</span></label>
            <input type="text" name="bed_no" value={formData.bed_no} onChange={handleChange} disabled={isDisabled} placeholder="e.g., A" />
          </div>
          <div className="input-box">
            <label>Room Type <span style={{color:'red'}}>*</span></label>
            <select name="room_type" value={formData.room_type} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Type</option>
              <option value="Single">Single Deluxe</option>
              <option value="Double">Double Sharing</option>
              <option value="Ward">General Ward</option>
              <option value="ICU">ICU</option>
            </select>
          </div>
        </div>

        <div className="form-section-header">
          <h3>Location & Billing</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Location / Floor</label>
            <input type="text" name="location" value={formData.location} onChange={handleChange} disabled={isDisabled} placeholder="e.g., 2nd Floor, West Wing" />
          </div>
          <div className="input-box">
            <label>Daily Rate (₹)</label>
            <input type="number" name="rate" value={formData.rate} onChange={handleChange} disabled={isDisabled} placeholder="0.00" />
          </div>
          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Available">Available</option>
              <option value="Occupied">Occupied</option>
              <option value="Maintenance">Under Maintenance</option>
              <option value="Blocked">Blocked</option>
            </select>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BedsMaster;